import React, { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import CustomInput from "../../common/customInput/customInput";
import CustomButton from "../../common/customButton/customButton";
import BackButton from "../../common/backButton/backButton";
import useCustomInput from "../../common/customInput/hook/useCustomInput";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useSharedState } from "../../context/formsContext";
import useChildRegistration from "./hook/useChildRegistration";
import UniformLoader from "../../common/uniformLoader/uniformLoader";
import { getProperty } from "../../utils/apiResponseHelper";
import { Helmet } from "react-helmet-async";

const ChildRegistration = ({ addChildDashboard = false, isGcse }) => {
  const { handleInfoClicked, infoClicked, setInfoClicked } =
    useChildRegistration();

  const {
    value,
    loading,
    setGrammarSchools,
    grammarSchools,
    setSchoolList,
    schoolList,
    setIsValidYear,
    isValidYear,
    prefix,
    setIsGcse,
    setCaptcha,
  } = useSharedState();

  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false);
  const [sessionCourseId, setSessionCourseId] = useState(null);
  const [gcseYearError, setGcseYearError] = useState("");
  const [childYearError, setChildYearError] = useState("");
  const [calculatedYear, setCalculatedYear] = useState(null);

  const {
    handleInputChange,
    errorMessage,
    nextClickChild,
    showCaptcha,
    showRegistrationSuccess,
  } =
    useCustomInput(addChildDashboard, sessionCourseId, isGcse);

  useEffect(() => {
    setIsGcse(isGcse);
  }, []);

  useEffect(() => {
    if (!addChildDashboard) {
      localStorage.removeItem("initalLoadComplete");
      localStorage.removeItem("isGcse");
      localStorage.removeItem("loginToken");
      localStorage.removeItem("mentorEmail");
      localStorage.removeItem("mentorId");
      localStorage.removeItem("previousPage");
      localStorage.removeItem("studentId");
      localStorage.removeItem("testData");
    }
  }, []);

  useEffect(() => {
    const courseId = localStorage.getItem("gcse");
    if (courseId === "true") {
      setSessionCourseId(true);
    } else {
      setSessionCourseId(false);
    }
  }, []);

  const isMounted = useRef(true);
  const recaptchaRef = useRef(null);

  const getGrammarSchools = async () => {
    const sessionLoginToken = localStorage.getItem("loginToken");
    try {
      const response = await fetch(
        `${prefix}/api-40/api40/Frontend/GetGrammarSchool?PostCode=b90%204ae&Distance=500%20`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionLoginToken}`,
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setGrammarSchools(data);
    } catch (error) {
      try {
        await fetch(`${prefix}/api-40/Log/Log`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionLoginToken}`,
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
          },
          body: JSON.stringify({
            Message: `Error retrieving schools: ${error.message}`,
            Time: new Date().toISOString(),
          }),
        });
      } catch (logError) {
      }

    }
  };

  useEffect(() => {
    getGrammarSchools();
  }, []);

  const calculateChildYear = (DOB) => {
    if (!DOB)
      return {
        childSchoolYear: null,
        isValid: false,
        gcseError: "",
        childError: "",
      };

    const [childYear, childMonth, childDay] = DOB.split("-").map(Number);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();

    const isBeforeSeptember =
      currentMonth < 9 || (currentMonth === 9 && currentDay < 1);

    const currentAcademicYear = isBeforeSeptember
      ? currentYear - 1
      : currentYear;

    const schoolStartYear =
      childMonth > 9 || (childMonth === 9 && childDay >= 1)
        ? childYear + 5
        : childYear + 4;

    const childSchoolYear = currentAcademicYear - schoolStartYear;

    let isValid = false;
    let gcseError = "";
    let childError = "";

    if (!sessionCourseId || !isGcse) {
      isValid = childSchoolYear === 4 || childSchoolYear === 5;
    } else {
      isValid = childSchoolYear === 10 || childSchoolYear === 11;
    }

    if ((sessionCourseId || isGcse) && !isValid) {
      gcseError = "Student year must be year 10 or 11";
    }

    if ((!sessionCourseId || !isGcse) && !isValid) {
      childError = "Child year must be year 4 or 5";
    }

    if (childSchoolYear > 13 || childSchoolYear < 1) {
      return { childSchoolYear: null, isValid, gcseError, childError };
    }

    return { childSchoolYear, isValid, gcseError, childError };
  };

  const handleDOBChange = (event) => {
    const dob = event.target.value;
    handleInputChange(event, "DOB");

    const { childSchoolYear, isValid, gcseError, childError } =
      calculateChildYear(dob);

    setIsValidYear(isValid);
    setGcseYearError(gcseError);
    setChildYearError(childError);
    setCalculatedYear(childSchoolYear);
  };

  useEffect(() => {
    setInfoClicked(false);
  }, [value.DOB]);

  const handleSchoolInputChange = (event) => {
    const input = event.target.value;
    handleInputChange(event, "school");

    // If input is empty, show all schools (or first 50 to avoid performance issues)
    if (!input.trim()) {
      const allSchools = Array.isArray(grammarSchools) ? grammarSchools.slice(0, 50) : [];
      setSchoolList(allSchools);
      return;
    }

    // Filter schools based on input
    const filtered = (Array.isArray(grammarSchools) ? grammarSchools : []).filter((school) => {
      // Get school name with fallback for case sensitivity and undefined values
      const schoolName = getProperty(school, 'schoolName') || school.SchoolName || school.schoolName || '';
      // Ensure schoolName is a string before calling toLowerCase
      if (!schoolName || typeof schoolName !== 'string') {
        return false;
      }
      return schoolName.toLowerCase().includes(input.toLowerCase());
    });

    setSchoolList(filtered);
  };

  const navigate = useNavigate();

  if (loading) {
    return (
      <UniformLoader
        title="Loading..."
        message="Please wait while we load the registration form"
        subMessage=""
      />
    );
  }

  if (showRegistrationSuccess && !addChildDashboard) {
    return (
      <>
        <Helmet>
          <title>Registration Successful | KSOL</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="w-full min-h-screen flex flex-col items-center justify-start bg-image p-4">
          <div className="w-full max-w-[80%] flex flex-col items-center p-6 xl:p-8">
            <div className="w-full md:w-3/5 p-6 text-white background-colour-verified flex flex-col justify-center items-center drop-shadow-lg">
              <h2 className="mt-2 mb-2">Registered Successfully!</h2>
              <p className="text-center">
                Your child details have been saved. Redirecting you to login...
              </p>
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mt-4" aria-label="Loading Spinner"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const handleRecaptchaChange = () => {
    if (recaptchaRef.current.getValue()) {
      setCaptcha(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Child Registration | KSOL</title>
        <meta name="description" content="Register your child for KSOL 11+ preparation resources and personalized learning." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full min-h-screen flex flex-col items-center justify-start bg-image p-4">
      <div className="w-full max-w-[80%] flex flex-col items-center p-6 xl:p-8">
        <div className="flex w-full justify-center ">
          {!addChildDashboard && (
            <>
              <div className="flex w-11/12 mb-4 gap-2">
                <div id="parent-select" className=" flex flex-col w-1/2 ">
                  <div
                    id="parent-box"
                    onClick={() => navigate("/")}
                    className="w-full h-4 mb-2 box-bg-border "
                  ></div>
                  <p>Step 1</p>
                  <p className="font-bold input-tags">Parent Details</p>
                </div>
                <div id="student-select" className=" flex flex-col w-1/2 ">
                  <div
                    id="student-box"
                    className="box-bg-border box-bg-color h-4 mb-2"
                  ></div>
                  <p>Step 2</p>
                  <p className="font-bold input-tags">Student Details</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="w-full flex flex justify-center  md:justify-center">
          <div className="w-11/12 flex flex-col ">
            <h3 className=" mb-4">
              {!addChildDashboard ? "Child Details" : "Student Details"}
            </h3>
            <div className="flex flex-wrap  md:grid grid-cols-2 gap-4">
              <div className="w-full">
                <p>
                  First Name <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  inputType={"childFirstName"}
                  type={"childFirstName"}
                  value={value.childFirstName}
                  placeholder={""}
                  onChange={(event) =>
                    handleInputChange(event, "childFirstName")
                  }
                  errorMessage={errorMessage.childFirstName || ""}
                />
              </div>

              <div className="w-full">
                <p>
                  Last Name <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  inputType={"childLastName"}
                  type={"childLastName"}
                  value={value.childLastName}
                  placeholder={""}
                  onChange={(event) =>
                    handleInputChange(event, "childLastName")
                  }
                  errorMessage={errorMessage.childLastName || ""}
                />
              </div>

              <div className="w-full">
                <div className="flex items-end">
                  <p>
                    DOB <span style={{ color: "red" }}>*</span>
                  </p>
                  <div className="ml-2 mb-1">
                    <FontAwesomeIcon
                      onClick={handleInfoClicked}
                      icon={faCircleInfo}
                      size="lg"
                      style={{ color: "blue" }}
                    />
                  </div>
                </div>
                {infoClicked && (
                  <p className="text-sm mb-2 mt-2 text-blue-600 ">
                    This helps us create the right test questions for you.
                  </p>
                )}
                <CustomInput
                  inputType="date"
                  type="date"
                  value={value.DOB}
                  placeholder=""
                  onChange={(event) => handleDOBChange(event)}
                  errorMessage={errorMessage.DOB || ""}
                />
                {calculatedYear && (
                  <p className="text-sm mb-2 mt-2 text-blue-600">
                    Your child is in Year {calculatedYear}
                  </p>
                )}

                {gcseYearError && (
                  <p className="text-sm mb-2 mt-2 text-red-600">
                    {gcseYearError}
                  </p>
                )}

                {childYearError && !isValidYear && (
                  <p className="text-sm mb-2 mt-2 text-red-600">
                    {childYearError}
                  </p>
                )}
              </div>
              <div className="w-full">
                <p>
                  Gender <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  placeholder={""}
                  type={"gender"}
                  inputType={"gender"}
                  name={"gender"}
                  isGenderDropdown={true}
                  value={value.gender}
                  onChange={(event) => handleInputChange(event, "gender")}
                  errorMessage={errorMessage.gender || ""}
                />
              </div>
              <div className="w-full">
                <p>
                  Username <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  inputType={"childUsername"}
                  type={"childUsername"}
                  value={value.childUsername}
                  placeholder={""}
                  onChange={(event) =>
                    handleInputChange(event, "childUsername")
                  }
                  errorMessage={errorMessage.childUsername || ""}
                />
              </div>
              <div className="w-full">
                <p>
                  {isGcse ? "Prospective University" : "Prospective School"}
                </p>
                <CustomInput
                  isGrammarSchool={true}
                  inputType={"school"}
                  type={"school"}
                  value={value.school}
                  placeholder={""}
                  onChange={(event) =>
                    !isGcse
                      ? handleSchoolInputChange(event)
                      : handleInputChange(event, "school")
                  }
                  errorMessage={errorMessage.childLastName || ""}
                />
              </div>
              <div className="w-full">
                <p>
                  Password <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  type={"childPassword"}
                  value={value.childPassword}
                  placeholder={""}
                  inputType="password"
                  onChange={(event) =>
                    handleInputChange(event, "childPassword")
                  }
                  errorMessage={errorMessage.childPassword || ""}
                />
              </div>
              <div className="w-full">
                <p>
                  Confirm Password <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  type={"childConfirmedPassword"}
                  value={value.childConfirmedPassword}
                  placeholder={""}
                  inputType="password"
                  onChange={(event) =>
                    handleInputChange(event, "childConfirmedPassword")
                  }
                  errorMessage={errorMessage.childConfirmedPassword || ""}
                />
              </div>
              {isGcse && (
                <div className="w-full lg:w/1/2">
                  <p>
                    Student Email <span style={{ color: "red" }}>*</span>
                  </p>
                  <CustomInput
                    type={"studentEmail"}
                    inputType={"studentEmail"}
                    value={value.studentEmail}
                    placeholder={""}
                    onChange={(event) =>
                      handleInputChange(event, "studentEmail")
                    }
                    errorMessage={errorMessage.studentEmail || ""}
                  />
                </div>
              )}
              <div className="flex flex-col items-start  md:flex-row md:items-center">
                <div className="col-span-2 mt-4 mb-6">
                  {!addChildDashboard ? (
                    <CustomButton
                      text={"Next"}
                      onClick={() => nextClickChild()}
                    />
                  ) : (
                    <CustomButton
                      text={"Add"}
                      onClick={() => nextClickChild()}
                    />
                  )}
                </div>
                {!addChildDashboard && (
                  <div className="mb-4 ml-0 md:ml-4">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LfZ8GQqAAAAAC7zXqVuNZYXO9iKzId467JrCdPq"
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ChildRegistration;
